import React, { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { apiClient } from "@/api/client"
import { ProviderDto } from "@/api/api-client"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetTrigger,
} from "@/components/ui/sheet"
import { Plus, Trash2, Pencil, Building2 } from "lucide-react"
import { toastSuccess } from "@/lib/toast"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { providerSchema, type ProviderFormData } from "@/lib/validationSchemas"
import { ScrollArea } from "@/components/ui/scroll-area"
import { useLanguage } from "@/hooks/useLanguage"

interface ProviderManagementProps {}

export const ProviderManagement: React.FC<ProviderManagementProps> = () => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  const text = t.providerManagement
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const [editingProvider, setEditingProvider] = useState<ProviderDto | null>(null)

  const { data: providers, isLoading } = useQuery({
    queryKey: ["providers"],
    queryFn: () => apiClient.getAllProviders(),
  })

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
  } = useForm<ProviderFormData>({
    resolver: zodResolver(providerSchema),
  })

  const createMutation = useMutation({
    mutationFn: (data: ProviderFormData) => apiClient.createProvider(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["providers"] })
      setIsSheetOpen(false)
      reset()
      toastSuccess(text.successCreate)
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: ProviderFormData) =>
      apiClient.updateProvider(editingProvider!.id, {
        name: data.name,
        description: data.description || "",
        logoUrl: data.logoUrl || "",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["providers"] })
      setIsSheetOpen(false)
      setEditingProvider(null)
      reset()
      toastSuccess(text.successUpdate)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiClient.deleteProvider(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["providers"] })
      toastSuccess(text.successDelete)
    },
  })

  const onSubmit = (data: ProviderFormData) => {
    if (editingProvider) {
      updateMutation.mutate(data)
    } else {
      createMutation.mutate(data)
    }
  }

  const handleEdit = (provider: ProviderDto) => {
    setEditingProvider(provider)
    setValue("name", provider.name)
    setValue("description", provider.description || "")
    setValue("logoUrl", provider.logoUrl || "")
    setIsSheetOpen(true)
  }

  return (
    <div className="space-y-10">
      <div className="flex flex-col items-start justify-between gap-6 border-b-2 border-foreground/5 pb-8 md:flex-row md:items-end">
        <div>
          <h2 className="mb-2 font-display text-4xl leading-none tracking-tighter text-foreground">
            {text.title}
          </h2>
          <p className="text-sm font-medium uppercase tracking-widest text-muted-foreground">
            {text.subtitle}
          </p>
        </div>

        <Sheet
          open={isSheetOpen}
          onOpenChange={(open) => {
            setIsSheetOpen(open)
            if (!open) {
              setEditingProvider(null)
              reset()
            }
          }}
        >
          <SheetTrigger asChild>
            <Button className="h-12 rounded-lg bg-primary px-8 font-display tracking-widest text-primary-foreground shadow-md hover:opacity-90">
              <Plus className="mr-2 h-4 w-4" />
              {text.add}
            </Button>
          </SheetTrigger>
          <SheetContent className="flex h-full w-full flex-col overflow-hidden border-l border-border bg-muted p-0 shadow-2xl sm:max-w-[500px]">
            <div className="flex h-full flex-col px-1">
              <SheetHeader className="border-b bg-background p-6">
                <SheetTitle className="font-display text-2xl tracking-widest">
                  {editingProvider ? text.edit : text.add}
                </SheetTitle>
              </SheetHeader>

              <ScrollArea className="flex-1">
                <div className="p-6">
                  <form id="provider-form" onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="label-industrial">
                        {text.name}
                      </Label>
                      <Input id="name" {...register("name")} className="h-12 text-base" />
                      {errors.name && (
                        <p className="text-xs font-medium text-destructive">
                          {errors.name.message}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description" className="label-industrial">
                        {text.description}
                      </Label>
                      <Textarea
                        id="description"
                        {...register("description")}
                        rows={5}
                        className="text-base"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="logoUrl" className="label-industrial">
                        {text.logoUrl}
                      </Label>
                      <Input
                        id="logoUrl"
                        {...register("logoUrl")}
                        placeholder="https://..."
                        className="h-12 text-base"
                      />
                      {errors.logoUrl && (
                        <p className="text-xs font-medium text-destructive">
                          {errors.logoUrl.message}
                        </p>
                      )}
                    </div>
                  </form>{" "}
                </div>
              </ScrollArea>

              <SheetFooter className="border-t bg-muted/10 p-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsSheetOpen(false)}
                  className="font-display tracking-widest"
                >
                  {text.cancel}
                </Button>
                <Button
                  type="submit"
                  form="provider-form"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="h-12 bg-primary px-8 font-display tracking-widest text-primary-foreground shadow-md hover:opacity-90"
                >
                  {createMutation.isPending || updateMutation.isPending ? "..." : text.save}
                </Button>
              </SheetFooter>
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <div className="border-none bg-transparent">
        <Table>
          <TableHeader>
            <TableRow className="border-b-2 border-border hover:bg-transparent">
              <TableHead className="px-0 font-display text-lg tracking-widest text-foreground">
                {text.name}
              </TableHead>
              <TableHead className="font-display text-lg tracking-widest text-foreground">
                {text.description}
              </TableHead>
              <TableHead className="px-0 text-right font-display text-lg tracking-widest text-foreground">
                {text.actions}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={3} className="py-20 text-center">
                  <div className="animate-pulse font-display text-2xl tracking-widest opacity-20">
                    {text.loading}
                  </div>
                </TableCell>
              </TableRow>
            ) : providers && providers.length > 0 ? (
              providers.map((provider) => (
                <TableRow
                  key={provider.id}
                  className="group border-b border-border transition-colors hover:bg-muted/5"
                >
                  <TableCell className="px-0 py-6">
                    <div className="flex items-center gap-4">
                      <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted/50 grayscale transition-all duration-500 group-hover:grayscale-0">
                        {provider.logoUrl ? (
                          <img
                            src={provider.logoUrl}
                            alt={provider.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <Building2 className="h-6 w-6 text-muted-foreground/30" />
                        )}
                      </div>
                      <span className="font-display text-xl tracking-wider text-foreground transition-colors group-hover:text-accent">
                        {provider.name}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="max-w-md font-medium text-muted-foreground">
                    {provider.description || "-"}
                  </TableCell>
                  <TableCell className="px-0 py-6 text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="transition-colors hover:bg-accent/10 hover:text-accent"
                        onClick={() => handleEdit(provider)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => {
                          if (confirm(text.deleteConfirm)) {
                            deleteMutation.mutate(provider.id)
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} className="empty-state py-32">
                  <Building2 className="mx-auto mb-4 h-12 w-12 text-muted-foreground opacity-10" />
                  <p className="font-display text-2xl tracking-widest text-muted-foreground opacity-20">
                    {text.empty}
                  </p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

